/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect} from 'react';
import Layout from '../components/layout/Layout';
import {BarChart,Bar,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,AreaChart,Area,PieChart,Pie,Cell,LineChart,Line} from 'recharts';
import api from '../services/api';
import {fmtCurrency,fmtDate} from '../utils/helpers';

const COLORS=['#2563eb','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];

function toCSV(rows:any[]):string{
  if(!rows||rows.length===0)return'';
  const cols=Object.keys(rows[0]);
  const esc=(v:any)=>{const s=v==null?'':String(v);return/[",\n]/.test(s)?`"${s.replace(/"/g,'""')}"`:s;};
  return[cols.join(','),...rows.map(r=>cols.map(c=>esc(r[c])).join(','))].join('\n');
}
function downloadCSV(filename:string,rows:any[]){
  if(!rows||rows.length===0)return;
  const blob=new Blob([toCSV(rows)],{type:'text/csv;charset=utf-8;'});
  const url=URL.createObjectURL(blob);
  const a=document.createElement('a');
  a.href=url;a.download=filename;document.body.appendChild(a);a.click();document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ReportsPage(){
  const [tab,setTab]=useState<'overview'|'revenue'|'opd'|'ipd'|'pharmacy'|'lab'>('overview');
  const [dateRange,setDateRange]=useState({from:new Date(Date.now()-30*86400000).toISOString().split('T')[0],to:new Date().toISOString().split('T')[0]});
  const [summary,setSummary]=useState<any>({});
  const [revenueData,setRevenueData]=useState<any[]>([]);
  const [patientData,setPatientData]=useState<any[]>([]);
  const [opdData,setOpdData]=useState<any[]>([]);
  const [ipdData,setIpdData]=useState<any[]>([]);
  const [pharmacyData,setPharmacyData]=useState<any[]>([]);
  const [labData,setLabData]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [quickReport,setQuickReport]=useState<{label:string;loading:boolean;rows:any[]}|null>(null);

  useEffect(()=>{
    api.getDashboardSummary().then(r=>setSummary(r.data||{})).catch(()=>{});
    loadReports();
  },[]);

  const loadReports=async()=>{
    setLoading(true);
    try{
      const[rev,pat,opd,ipd,pharm,lab]=await Promise.all([
        api.getRevenueReport({from:dateRange.from,to:dateRange.to}),
        api.getPatientReport({from:dateRange.from,to:dateRange.to}),
        api.getOPDReport({from:dateRange.from,to:dateRange.to}),
        api.getIPDReport({from:dateRange.from,to:dateRange.to}),
        api.getPharmacyReport({from:dateRange.from,to:dateRange.to}),
        api.getLabReport?.({from:dateRange.from,to:dateRange.to}).catch(()=>({data:[]})),
      ]);
      setRevenueData(rev.data||[]);
      setPatientData(pat.data||[]);
      setOpdData(opd.data||[]);
      setIpdData(ipd.data||[]);
      setPharmacyData(pharm.data||[]);
      setLabData(lab.data||[]);
    }catch{}finally{setLoading(false);}
  };

  const runQuickReport=async(label:string,fetcher:()=>Promise<any>)=>{
    setQuickReport({label,loading:true,rows:[]});
    try{
      const r=await fetcher();
      const data=r?.data;
      const rows=Array.isArray(data)?data:(data&&typeof data==='object'?[data]:[]);
      setQuickReport({label,loading:false,rows});
    }catch{setQuickReport({label,loading:false,rows:[]});}
  };

  const REPORT_CARDS=[
    {label:'Patient Registration',icon:'👥',desc:'Daily registrations, demographics, gender distribution',color:'blue',
      run:()=>runQuickReport('Patient Registration',()=>api.getPatientReport({from:dateRange.from,to:dateRange.to}))},
    {label:'Revenue Analysis',icon:'💰',desc:'Collection, pending, payment methods breakdown',color:'green',run:()=>setTab('revenue')},
    {label:'OPD Statistics',icon:'🏥',desc:'Consultations, doctor-wise analysis, diagnosis trends',color:'purple',run:()=>setTab('opd')},
    {label:'IPD Report',icon:'🛏️',desc:'Admissions, discharges, occupancy, length of stay',color:'amber',run:()=>setTab('ipd')},
    {label:'Pharmacy Sales',icon:'💊',desc:'Drug sales, revenue, top drugs, expiry report',color:'teal',run:()=>setTab('pharmacy')},
    {label:'Laboratory Report',icon:'🔬',desc:'Tests performed, turnaround time, critical values',color:'red',run:()=>setTab('lab')},
    {label:'Radiology Report',icon:'🩻',desc:'Orders, modality-wise stats, pending reports',color:'indigo',
      run:()=>runQuickReport('Radiology Report',()=>api.getRadiologyReport2?.({from:dateRange.from,to:dateRange.to}))},
    {label:'Blood Bank Report',icon:'🩸',desc:'Stock levels, requests, transfusions',color:'pink',
      run:()=>runQuickReport('Blood Bank Report',()=>api.getBloodBankStats?.())},
    {label:'Doctor Performance',icon:'👨‍⚕️',desc:'Consultations per doctor, revenue generated',color:'cyan',
      run:()=>runQuickReport('Doctor Performance',()=>api.getDoctorPerformanceReport?.({from:dateRange.from,to:dateRange.to}))},
    {label:'HRMS Report',icon:'👔',desc:'Attendance, leaves, payroll summary',color:'orange',
      run:()=>runQuickReport('HRMS Report',()=>api.getHRMSStats?.())},
    {label:'Insurance/TPA',icon:'📋',desc:'Claims submitted, approved, pending',color:'slate',
      run:()=>runQuickReport('Insurance/TPA',()=>api.getInsuranceReport?.({from:dateRange.from,to:dateRange.to}))},
    {label:'MIS Report',icon:'📊',desc:'Management information system daily summary',color:'blue',
      run:()=>runQuickReport('MIS Report',()=>api.getMisReport?.(dateRange.from,dateRange.to))},
  ];

  // Gender breakdown from real patient-report rows — merge case-variant values (e.g. "MALE" vs "Male")
  const genderDist=(()=>{
    const merged:Record<string,number>={};
    patientData.forEach((p:any)=>{
      const label=String(p.gender||'Unknown').trim();
      const key=label.toUpperCase();
      const niceLabel=key==='MALE'?'Male':key==='FEMALE'?'Female':key==='OTHER'?'Other':label;
      merged[niceLabel]=(merged[niceLabel]||0)+parseInt(p.count||0);
    });
    return Object.entries(merged).map(([name,value],i)=>({name,value,color:COLORS[i%COLORS.length]}));
  })();

  const TAB_TABLE_CONFIG:Record<string,{rows:any[];cols:{key:string;label:string;fmt?:(v:any)=>any}[]}>={
    opd:{rows:opdData,cols:[{key:'date',label:'Date',fmt:fmtDate},{key:'consultations',label:'Consultations'},{key:'unique_patients',label:'Unique Patients'}]},
    ipd:{rows:ipdData,cols:[{key:'date',label:'Date',fmt:fmtDate},{key:'admissions',label:'Admissions'},{key:'discharges',label:'Discharges'}]},
    pharmacy:{rows:pharmacyData,cols:[{key:'date',label:'Date',fmt:fmtDate},{key:'sales',label:'Sales'},{key:'revenue',label:'Revenue',fmt:fmtCurrency}]},
    lab:{rows:labData,cols:[{key:'date',label:'Date',fmt:fmtDate},{key:'requests',label:'Requests'},{key:'completed',label:'Completed'}]},
  };

  return(
    <Layout title="Reports & Analytics">
      <div className="flex gap-2 mb-5 flex-wrap">
        {[['overview','📊 Overview'],['revenue','💰 Revenue'],['opd','🏥 OPD'],['ipd','🛏️ IPD'],['pharmacy','💊 Pharmacy'],['lab','🔬 Lab']].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t as any)} className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap border-2 flex-shrink-0 transition-all ${tab===t?'bg-[#02357A] text-white border-[#02357A] shadow-lg shadow-[#02357A]/20':'bg-white text-slate-600 border-slate-200 hover:border-[#02357A] hover:text-[#02357A]'}`}>{l}</button>
        ))}
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2"><label className="text-sm font-medium text-slate-600">From:</label><input type="date" value={dateRange.from} onChange={e=>setDateRange({...dateRange,from:e.target.value})} className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 w-40 text-sm"/></div>
        <div className="flex items-center gap-2"><label className="text-sm font-medium text-slate-600">To:</label><input type="date" value={dateRange.to} onChange={e=>setDateRange({...dateRange,to:e.target.value})} className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 w-40 text-sm"/></div>
        <button onClick={loadReports} disabled={loading} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed">{loading?'Loading...':'Generate Report'}</button>
        <div className="flex gap-2 ml-auto">
          <button onClick={()=>{
            const rows=tab==='revenue'?revenueData:tab==='overview'?patientData:(TAB_TABLE_CONFIG[tab]?.rows||[]);
            downloadCSV(`${tab}-report-${dateRange.from}_to_${dateRange.to}.csv`,rows);
          }} disabled={loading} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50">📥 Export Excel</button>
          <button onClick={()=>window.print()} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50">🖨️ Print PDF</button>
        </div>
      </div>

      {tab==='overview'&&(
        <div className="space-y-5">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {l:'Total Patients',v:summary.patients?.total||0,c:'blue',i:'👥'},
              {l:"Today's OPD",v:summary.opd?.today||0,c:'green',i:'🏥'},
              {l:'Active IPD',v:summary.ipd?.admitted||0,c:'amber',i:'🛏️'},
              {l:'Month Revenue',v:fmtCurrency(summary.billing?.month_collection||0),c:'purple',i:'💰'},
            ].map(s=>(
              <div key={s.l} className={`card p-5 flex items-center gap-3`}>
                <span className="text-3xl">{s.i}</span>
                <div><p className="text-slate-500 text-xs">{s.l}</p><p className={`text-2xl font-bold text-${s.c}-600`}>{s.v}</p></div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 card p-5">
              <h3 className="font-semibold text-slate-700 mb-4">Revenue Trend (Billed vs Collected)</h3>
              {revenueData.length===0?<div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">No revenue data for this range</div>:(
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={[...revenueData].reverse()}>
                  <defs>
                    <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563eb" stopOpacity={0.15}/><stop offset="95%" stopColor="#2563eb" stopOpacity={0}/></linearGradient>
                    <linearGradient id="gb" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.15}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="date" tickFormatter={v=>fmtDate(v)} tick={{fontSize:11}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:11}} axisLine={false} tickLine={false} tickFormatter={v=>`₹${(v/1000).toFixed(0)}K`}/>
                  <Tooltip labelFormatter={v=>fmtDate(v)} formatter={(v:any)=>fmtCurrency(+v)}/>
                  <Area type="monotone" dataKey="total_billed" stroke="#2563eb" strokeWidth={2} fill="url(#gc)" name="Billed"/>
                  <Area type="monotone" dataKey="total_collected" stroke="#10b981" strokeWidth={2} fill="url(#gb)" name="Collected"/>
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-semibold text-slate-700 mb-4">Patients by Gender</h3>
              {genderDist.length===0?<div className="h-[160px] flex items-center justify-center text-slate-400 text-sm">No patient data for this range</div>:(<>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart><Pie data={genderDist} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                  {genderDist.map((d,i)=><Cell key={i} fill={d.color}/>)}
                </Pie><Tooltip/></PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {genderDist.map(d=>(
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{background:d.color}}/><span className="text-slate-600">{d.name}</span></div>
                    <span className="font-semibold text-slate-700">{d.value}</span>
                  </div>
                ))}
              </div>
              </>)}
            </div>
          </div>

          {/* Report Cards */}
          <div>
            <h3 className="font-semibold text-slate-700 mb-4">Available Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {REPORT_CARDS.map(r=>(
                <div key={r.label} className={`card p-4 hover:shadow-md transition-all cursor-pointer border-t-4 border-${r.color}-400`}>
                  <div className="flex items-center gap-2 mb-2"><span className="text-2xl">{r.icon}</span><h4 className="font-semibold text-slate-700 text-sm">{r.label}</h4></div>
                  <p className="text-slate-500 text-xs mb-3">{r.desc}</p>
                  <div className="flex gap-2">
                    <button onClick={r.run} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed text-xs py-1 px-2.5 flex-1">Generate</button>
                    <button onClick={()=>{api.logReportPrint?.({reportType:r.label}).catch(()=>{});window.print();}} title="Log & print" className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs py-1 px-2">📅</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab==='revenue'&&(
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-semibold text-slate-700 mb-4">Revenue Report ({dateRange.from} to {dateRange.to})</h3>
          {revenueData.length>0?(
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData.slice(0,14)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="date" tickFormatter={v=>fmtDate(v)} tick={{fontSize:11}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:11}} axisLine={false} tickLine={false} tickFormatter={v=>`₹${(+v/1000).toFixed(0)}K`}/>
                  <Tooltip labelFormatter={v=>fmtDate(v)} formatter={(v:any)=>fmtCurrency(+v)}/>
                  <Bar dataKey="total_billed" name="Billed" fill="#2563eb" radius={[4,4,0,0]}/>
                  <Bar dataKey="total_collected" name="Collected" fill="#10b981" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50"><tr><th className="px-4 py-2 text-left text-xs text-slate-500">Date</th><th className="px-4 py-2 text-right text-xs text-slate-500">Bills</th><th className="px-4 py-2 text-right text-xs text-slate-500">Billed</th><th className="px-4 py-2 text-right text-xs text-slate-500">Collected</th><th className="px-4 py-2 text-right text-xs text-slate-500">Pending</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {revenueData.map((r:any,i)=>(
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium">{fmtDate(r.date)}</td>
                        <td className="px-4 py-2 text-right">{r.bill_count||0}</td>
                        <td className="px-4 py-2 text-right text-[#02357A] font-medium">{fmtCurrency(+r.total_billed||0)}</td>
                        <td className="px-4 py-2 text-right text-green-600 font-medium">{fmtCurrency(+r.total_collected||0)}</td>
                        <td className="px-4 py-2 text-right text-red-500">{fmtCurrency(+r.total_pending||0)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold">
                    <tr>
                      <td className="px-4 py-2">Total</td>
                      <td className="px-4 py-2 text-right">{revenueData.reduce((s:number,r:any)=>s+(+r.bill_count||0),0)}</td>
                      <td className="px-4 py-2 text-right text-[#02357A]">{fmtCurrency(revenueData.reduce((s:number,r:any)=>s+(+r.total_billed||0),0))}</td>
                      <td className="px-4 py-2 text-right text-green-600">{fmtCurrency(revenueData.reduce((s:number,r:any)=>s+(+r.total_collected||0),0))}</td>
                      <td className="px-4 py-2 text-right text-red-500">{fmtCurrency(revenueData.reduce((s:number,r:any)=>s+(+r.total_pending||0),0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ):<div className="py-12 text-center"><span className="text-5xl block mb-3">💰</span><p className="text-slate-500">Click "Generate Report" to load data</p></div>}
        </div>
      )}

      {['opd','ipd','pharmacy','lab'].includes(tab)&&(()=>{
        const cfg=TAB_TABLE_CONFIG[tab];
        return(
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-semibold text-slate-700 mb-4">{tab.toUpperCase()} Report ({dateRange.from} to {dateRange.to})</h3>
          {cfg.rows.length===0?(
            <div className="py-12 text-center"><span className="text-5xl block mb-3">📊</span><p className="text-slate-500">{loading?'Loading…':'No data for this range'}</p></div>
          ):(
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50"><tr>{cfg.cols.map(c=><th key={c.key} className="px-4 py-2 text-left text-xs text-slate-500">{c.label}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {cfg.rows.map((row:any,i:number)=>(
                    <tr key={i} className="hover:bg-slate-50">
                      {cfg.cols.map(c=><td key={c.key} className="px-4 py-2">{c.fmt?c.fmt(row[c.key]):(row[c.key]??'—')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        );
      })()}

      {quickReport&&(
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={()=>setQuickReport(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">{quickReport.label}</h3>
              <button onClick={()=>setQuickReport(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
            </div>
            <div className="p-4 overflow-auto">
              {quickReport.loading?(
                <p className="text-slate-400 text-sm text-center py-8">Loading…</p>
              ):quickReport.rows.length===0?(
                <p className="text-slate-400 text-sm text-center py-8">No data available for this report</p>
              ):(
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50"><tr>{Object.keys(quickReport.rows[0]).map(k=><th key={k} className="px-3 py-2 text-left text-xs text-slate-500 whitespace-nowrap">{k}</th>)}</tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {quickReport.rows.map((row:any,i:number)=>(
                      <tr key={i}>{Object.keys(quickReport.rows[0]).map(k=><td key={k} className="px-3 py-2 whitespace-nowrap">{typeof row[k]==='object'&&row[k]!==null?JSON.stringify(row[k]):String(row[k]??'—')}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
